// repoToggle.ts
import { LocalRepo } from "./LocalRepo";
import type { IUser, IGame } from "./IUser";
import { RealtimeChannel } from "@supabase/supabase-js";
import { GameRow } from "./data";


// CRA: set REACT_APP_USE_SUPABASE=1 to enable supabase
const USE_SUPABASE = process.env.REACT_APP_USE_SUPABASE;// === "1";
const CUR_ID_KEY = "skeech_currentUserId";
const CUR_NAME_KEY = "skeech_currentUserName";

export interface RepoAsync {
  getUserByName(name: string): Promise<IUser | null>;
  getUserById(id: string): Promise<IUser | null>;
  upsertUser(user: IUser): Promise<IUser>;
  updateUser(userId: string, userData: Partial<IUser>): Promise<IUser>;
  addGame(g: IGame): Promise<void>;
  updateGame(gameId: string, gameData: Partial<IGame>): Promise<void>;
  listGamesByUser(userId: string): Promise<IGame[]>;
  listCompletedMultiplayerGamesByUser(userId: string): Promise<any[]>;
  listActiveGamesByUser(userId: string): Promise<any[]>;
  createLobby(name: string, presenceKey: string): Promise<RealtimeChannel>;
  getUserPreferences(userId: string): Promise<any>;
  upsertUserPreferences(userId: string, prefs: any): Promise<any>;
  getUnfinishedGameById(gameId: string): Promise<GameRow | null>;
  getGameById(gameId: string): Promise<any | null>;
  forfeitGame(gameId: string, forfeiterUserId: string): Promise<void>;
  getUserRollHistories(userId: string): Promise<number[]>;
  calculateUserPPR(userId: string): Promise<number>;
  calculateWinLoss(userId: string, options?: { excludeTournaments?: boolean; excludeAI?: boolean }): Promise<{ wins: number; losses: number; gamesPlayed: number }>;
  createTournament(params: { name: string; creatorId: string; joinCode: string; maxPlayers?: number; type?: 'bracket' | 'round_robin' }): Promise<any>;
  joinTournamentByCode(userId: string, joinCode: string): Promise<{ tournament: any; player: any }>;
  listUserTournaments(userId: string): Promise<any[]>;
  startTournamentWithBracket(tournamentId: string): Promise<any>;
  recordTournamentMatchResult(params: { tournamentId: string; round: number; matchIndex: number; aScore: number; bScore: number; winnerUserId: string }): Promise<any>;
  applyBracketAutoAdvances(tournamentId: string): Promise<any>;
  upsertTournamentPlayerStats(tournamentId: string, userId: string, delta: any): Promise<any>;
  getUserTournamentParticipations(userId: string): Promise<any[]>;
  getTournamentPlayersWithStats(tournamentId: string): Promise<any[]>;
  saveTournamentAwardWinners(tournamentId: string, awards: any): Promise<void>;
}

class LocalRepoAsyncAdapter implements RepoAsync {
  constructor(private inner = new LocalRepo()) {}
  async createLobby(name: string, presenceKey: string): Promise<RealtimeChannel> {
    throw new Error("Method not implemented.");
  }
  async getUserByName(name: string) { return this.inner.getUserByName(name); }
  async getUserById(id: string)     { return this.inner.getUserById(id); }
  async upsertUser(user: IUser)     { return this.inner.upsertUser(user); }
  async updateUser(userId: string, userData: Partial<IUser>) {
    const user = await this.inner.getUserById(userId);
    if (!user) throw new Error('User not found');
    const updatedUser = { ...user, ...userData };
    return this.inner.upsertUser(updatedUser);
  }
  async addGame(g: IGame)           { this.inner.addGame(g); }
  async updateGame(gameId: string, gameData: Partial<IGame>): Promise<void> {
    console.warn("LocalRepoAsyncAdapter.updateGame not implemented.");
  }
  async listGamesByUser(userId: string) { return this.inner.listGamesByUser(userId); }
  async listCompletedMultiplayerGamesByUser(userId: string): Promise<any[]> {
    console.warn("LocalRepoAsyncAdapter.listCompletedMultiplayerGamesByUser not implemented.");
    return [];
  }
  async listActiveGamesByUser(userId: string): Promise<any[]> {
    console.warn("LocalRepoAsyncAdapter.listActiveGamesByUser not implemented.");
    return [];
  }
  async getUserPreferences(userId: string) {
    // For local repo, return null (no preferences stored locally)
    return Promise.resolve(null);
  }
  async upsertUserPreferences(userId: string, prefs: any) {
    // For local repo, just return the prefs (no actual storage)
    return Promise.resolve(prefs);
  }
  async getUnfinishedGameById(gameId: string): Promise<GameRow | null> {
    console.warn("LocalRepoAsyncAdapter.getUnfinishedGameById not implemented.");
    return null;
  }
  async getGameById(gameId: string): Promise<any | null> {
    console.warn("LocalRepoAsyncAdapter.getGameById not implemented.");
    return null;
  }
  async forfeitGame(gameId: string, forfeiterUserId: string): Promise<void> {
    console.warn("LocalRepoAsyncAdapter.forfeitGame not implemented.");
  }
  async getUserRollHistories(userId: string): Promise<number[]> {
    console.warn("LocalRepoAsyncAdapter.getUserRollHistories not implemented.");
    return [];
  }
  async calculateUserPPR(userId: string): Promise<number> {
    console.warn("LocalRepoAsyncAdapter.calculateUserPPR not implemented.");
    return 0;
  }
  async calculateWinLoss(userId: string, options?: { excludeTournaments?: boolean; excludeAI?: boolean }): Promise<{ wins: number; losses: number; gamesPlayed: number }> {
    console.warn("LocalRepoAsyncAdapter.calculateWinLoss not implemented.");
    return { wins: 0, losses: 0, gamesPlayed: 0 };
  }
  async createTournament(params: { name: string; creatorId: string; joinCode: string; maxPlayers?: number; type?: 'bracket' | 'round_robin' }): Promise<any> {
    throw new Error('Tournaments not available in Local mode');
  }
  async joinTournamentByCode(userId: string, joinCode: string): Promise<{ tournament: any; player: any }> {
    throw new Error('Tournaments not available in Local mode');
  }
  async listUserTournaments(userId: string): Promise<any[]> {
    return [];
  }
  async startTournamentWithBracket(tournamentId: string): Promise<any> {
    throw new Error('Tournaments not available in Local mode');
  }
  async recordTournamentMatchResult(): Promise<any> { throw new Error('Tournaments not available in Local mode'); }
  async applyBracketAutoAdvances(): Promise<any> { return null; }
  async upsertTournamentPlayerStats(): Promise<any> { return null; }
  async getUserTournamentParticipations(userId: string): Promise<any[]> { return []; }
  async getTournamentPlayersWithStats(tournamentId: string): Promise<any[]> { return []; }
  async saveTournamentAwardWinners(): Promise<void> { return; }
}

export class SupabaseRepoAdapter implements RepoAsync {
  public sb: any;
  constructor() {
    // CommonJS require so CRA won’t complain if data.ts isn’t present in some env
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require("./data");
    this.sb = new mod.SupabaseRepo();
  }
  private toUser(u: any): IUser {
    return {
      id: u.id,
      userName: u.user_name,
      wins: u.wins ?? 0,
      losses: u.losses ?? 0,
      numOfSkeeches: u.num_of_skeeches ?? 0,
      pointsTotal: u.points_total ?? 0,
      rollsTotal: u.rolls_total ?? 0,
      roundsTotal: u.rounds_total ?? 0,
      gamesPlayed: u.games_played ?? 0,
      avgPPR: Number(u.avg_ppr ?? 0),
      createdAt: u.created_at ? Date.parse(u.created_at) : Date.now(),
      updatedAt: u.updated_at ? Date.parse(u.updated_at) : Date.now(),
      profile_image_url: u.profile_image_url ?? undefined,
      current_game_rolls: [] // Always start fresh
    };
  }
  private toGame(g: any): IGame {
    return {
      id: g.id,
      isSoloGame: !!g.is_solo_game,
      p1UserId: g.p1_user_id ?? null,
      p2UserId: g.p2_user_id ?? null,
      p1Score: g.p1_score,
      p2Score: g.p2_score ?? null,
      rounds: g.rounds,
      rolls: g.rolls,
      points: g.points,
      skeeches: g.skeeches ?? 0,
      timeStamp: g.time_stamp ? Date.parse(g.time_stamp) : Date.now(),
      status: g.status ?? undefined,
      winnerId: g.winner_id ?? undefined,
      game_state_json: g.game_state_json ?? undefined,
      p1_roll_history: g.p1_roll_history ?? [],
      p2_roll_history: g.p2_roll_history ?? [],
      tournament_id: g.tournament_id ?? undefined,
    };
  }
  async getUserByName(name: string) {
    const row = await this.sb.getUserByName(name);
    return row ? this.toUser(row) : null;
  }
  async getUserById(id: string) {
    const row = await this.sb.getUserById(id);
    return row ? this.toUser(row) : null;
  }
  async upsertUser(user: IUser) {
    const existing = await this.getUserByName(user.userName);
    if (!existing) {
      const row = await this.sb.upsertUser(user.userName);
      return this.toUser(row);
    }
    const upd = {
      id: existing.id,
      user_name: existing.userName,
      // NOTE: wins, losses, and games_played are calculated on-the-fly, not stored in DB
      num_of_skeeches: user.numOfSkeeches,
      points_total: user.pointsTotal,
      rolls_total: user.rollsTotal,
      rounds_total: user.roundsTotal,
      avg_ppr: user.avgPPR,
    };
    const row = await this.sb.updateUser(upd);
    return this.toUser(row);
  }
  async updateUser(userId: string, userData: Partial<IUser>) {
    // This is a partial update, so we only convert the fields that are present.
    const upd: any = { id: userId };

    // Map IUser fields to UserRow fields (snake_case)
    if (userData.userName !== undefined) upd.user_name = userData.userName;
    // NOTE: wins, losses, and gamesPlayed are calculated on-the-fly, not stored in DB
    if (userData.numOfSkeeches !== undefined) upd.num_of_skeeches = userData.numOfSkeeches;
    if (userData.pointsTotal !== undefined) upd.points_total = userData.pointsTotal;
    if (userData.rollsTotal !== undefined) upd.rolls_total = userData.rollsTotal;
    if (userData.roundsTotal !== undefined) upd.rounds_total = userData.roundsTotal;
    if (userData.avgPPR !== undefined) upd.avg_ppr = userData.avgPPR;
    // Handle profile_image_url, allowing it to be set to null
    if (Object.prototype.hasOwnProperty.call(userData, 'profile_image_url')) {
      upd.profile_image_url = userData.profile_image_url;
    }

    const row = await this.sb.updateUser(upd);
    return this.toUser(row);
  }
  async addGame(g: IGame) {
    // Pass through optional tournament context if present on the game object (index.tsx sends these for tournament matches)
    const anyGame: any = g as any;
    const payload: any = {
      id: g.id, // Pass the ID from the game object
      is_solo_game: g.isSoloGame,
      p1_user_id: g.p1UserId,
      p2_user_id: g.p2UserId,
      p1_score: g.p1Score,
      p2_score: g.p2Score,
      rounds: g.rounds,
      rolls: g.rolls,
      points: g.points,
      skeeches: g.skeeches,
      status: g.status,
      // Ensure initial game_state_json is established at creation
      game_state_json: (anyGame as any).game_state_json ?? undefined,
    };
    if (anyGame.winnerId !== undefined) payload.winner_id = anyGame.winnerId;
    // Include tournament fields when available
    if (anyGame.tournament_id) payload.tournament_id = anyGame.tournament_id;
    if (anyGame.tournament_round !== undefined) payload.tournament_round = anyGame.tournament_round;
    if (anyGame.tournament_match !== undefined) payload.tournament_match = anyGame.tournament_match;
    await this.sb.addGame(payload);
  }
  async updateGame(gameId: string, g: Partial<IGame>) {
    const payload: Partial<GameRow> = {
      p1_score: g.p1Score,
      p2_score: g.p2Score,
      rounds: g.rounds,
      rolls: g.rolls,
      skeeches: g.skeeches,
      status: g.status,
      game_state_json: g.game_state_json,
      p1_roll_history: g.p1_roll_history,
      p2_roll_history: g.p2_roll_history,
      winner_id: g.winnerId,
    };
    // Filter out undefined values so we only update fields that were passed in
    Object.keys(payload).forEach(key => (payload as any)[key] === undefined && delete (payload as any)[key]);
    await this.sb.updateGame(gameId, payload);
  }
  async listGamesByUser(userId: string) {
    const rows = await this.sb.listGamesByUser(userId);
    return (rows || []).map((r: any) => this.toGame(r));
  }

  async listCompletedMultiplayerGamesByUser(userId: string) {
    // Return enriched rows with p1/p2 user info for UI display (matches listActiveGamesByUser shape)
    return await this.sb.listCompletedMultiplayerGamesByUser(userId);
  }

  async listActiveGamesByUser(userId: string): Promise<any[]> {
    return await this.sb.listActiveGamesByUser(userId);
  }

  async createLobby(name: string, presenceKey: string) {
    return this.sb.createChannel(name, presenceKey);
  }

  async getUserPreferences(userId: string) {
    return await this.sb.getUserPreferences(userId);
  }

  async upsertUserPreferences(userId: string, prefs: any) {
    return await this.sb.upsertUserPreferences(userId, prefs);
  }

  async getUnfinishedGameById(gameId: string): Promise<GameRow | null> {
    return await this.sb.getUnfinishedGameById(gameId);
  }

  async getGameById(gameId: string): Promise<any | null> {
    return await this.sb.getGameById(gameId);
  }

  async forfeitGame(gameId: string, forfeiterUserId: string): Promise<void> {
    await this.sb.forfeitGame(gameId, forfeiterUserId);
  }

  async getUserRollHistories(userId: string): Promise<number[]> {
    return await this.sb.getUserRollHistories(userId);
  }

  async calculateUserPPR(userId: string): Promise<number> {
    return await this.sb.calculateUserPPR(userId);
  }
  async calculateWinLoss(userId: string, options?: { excludeTournaments?: boolean; excludeAI?: boolean }): Promise<{ wins: number; losses: number; gamesPlayed: number }> {
    return await this.sb.calculateWinLoss(userId, options);
  }
  async createTournament(params: { name: string; creatorId: string; joinCode: string; maxPlayers?: number; type?: 'bracket' | 'round_robin' }): Promise<any> {
    return await this.sb.createTournament(params);
  }
  async joinTournamentByCode(userId: string, joinCode: string): Promise<{ tournament: any; player: any }> {
    return await this.sb.joinTournamentByCode(userId, joinCode);
  }
  async listUserTournaments(userId: string): Promise<any[]> {
    return await this.sb.listUserTournaments(userId);
  }
  async startTournamentWithBracket(tournamentId: string): Promise<any> {
    return await this.sb.startTournamentWithBracket(tournamentId);
  }
  async recordTournamentMatchResult(params: { tournamentId: string; round: number; matchIndex: number; aScore: number; bScore: number; winnerUserId: string }): Promise<any> {
    return await this.sb.recordTournamentMatchResult(params);
  }
  async applyBracketAutoAdvances(tournamentId: string): Promise<any> {
    return await this.sb.applyBracketAutoAdvances(tournamentId);
  }
  async upsertTournamentPlayerStats(tournamentId: string, userId: string, delta: any): Promise<any> {
    return await this.sb.upsertTournamentPlayerStats(tournamentId, userId, delta);
  }
  async getUserTournamentParticipations(userId: string): Promise<any[]> {
    return await this.sb.getUserTournamentParticipations(userId);
  }
  async getTournamentPlayersWithStats(tournamentId: string): Promise<any[]> {
    return await this.sb.getTournamentPlayersWithStats(tournamentId);
  }
  async saveTournamentAwardWinners(tournamentId: string, awards: any): Promise<void> {
    await this.sb.saveTournamentAwardWinners(tournamentId, awards);
  }
}

export function createRepo(): RepoAsync {
  if (USE_SUPABASE) {
    try { return new SupabaseRepoAdapter(); }
    catch (e) { /* Supabase unavailable, falling back to Local */ }
  }
  return new LocalRepoAsyncAdapter();
}

// Remember-me helpers (kept here so both repos can use the same behavior)
export function rememberUserLocal(id: string, name: string) {
  localStorage.setItem(CUR_ID_KEY, id);
  localStorage.setItem(CUR_NAME_KEY, name);
}
export function forgetUserLocal() {
  localStorage.removeItem(CUR_ID_KEY);
  localStorage.removeItem(CUR_NAME_KEY);
}
export function getRememberedName() { return localStorage.getItem(CUR_NAME_KEY); }
